import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { errMsg } from "../lib/errors";
import { readTopicFromSearch, persistTopic, topicLabel } from "../lib/qaTopic";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTopic = readTopicFromSearch(searchParams.toString());
  const [topicSlug] = useState(initialTopic);
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [baaAccepted, setBaaAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Persist the selected topic the moment the user lands here so it survives
  // even if they close/re-open the tab mid-signup. Cleared after they land
  // on Dashboard and either continue or dismiss the topic.
  useEffect(() => {
    if (topicSlug) persistTopic(topicSlug);
  }, [topicSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!baaAccepted) {
      toast.error("Please accept the Business Associate Agreement to continue");
      return;
    }
    setLoading(true);
    try {
      await signup(form.email, form.password, form.full_name, true);
      toast.success("Account created");
      // Preserve the pre-selected topic across authentication by forwarding
      // it as a query param — Dashboard picks it up from URL or localStorage.
      nav(topicSlug ? `/dashboard?topic=${topicSlug}` : "/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Signup failed"));
    } finally {
      setLoading(false);
    }
  };

  const fld = (name, type = "text", autoComplete = "off") => (
    <input
      type={type}
      required
      autoComplete={autoComplete}
      value={form[name]}
      onChange={(e) => setForm({ ...form, [name]: e.target.value })}
      data-testid={`signup-${name}`}
      className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] focus:border-[var(--pa-gold)] focus:outline-none p-3"
    />
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--pa-bg)" }}>
      <header className="px-6 lg:px-12 py-6 shrink-0">
        <Link to="/" className="flex items-baseline gap-2" data-testid="signup-brand">
          <span className="font-serif text-2xl text-[var(--pa-text)]">Prostho</span>
          <span className="font-serif text-2xl text-[var(--pa-gold)]">AI</span>
        </Link>
      </header>
      <main className="flex-1 w-full px-6 py-8 sm:py-16">
        <div className="w-full max-w-md mx-auto">
          <div className="kicker mb-3">Create Account</div>
          <h1 className="font-serif text-4xl text-[var(--pa-text)]">Get started</h1>
          <p className="mt-3 text-sm text-[var(--pa-text-muted)]">
            Specialist-grade prosthodontic guidance, ready in seconds.
          </p>
          {topicSlug && (
            <div
              data-testid="signup-topic-chip"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--pa-gold)] bg-[var(--pa-gold)]/10 px-4 py-2 text-xs uppercase tracking-[0.14em] text-[var(--pa-gold)] font-semibold"
            >
              <span aria-hidden="true">✦</span>
              <span>Continuing with:&nbsp;</span>
              <span className="normal-case tracking-normal text-[var(--pa-text)]">
                {topicLabel(topicSlug)}
              </span>
            </div>
          )}
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="signup-form">
            <div>
              <label className="kicker block mb-2">Full name</label>
              {fld("full_name", "text", "name")}
            </div>
            <div>
              <label className="kicker block mb-2">Email</label>
              {fld("email", "email", "username")}
            </div>
            <div>
              <label className="kicker block mb-2">Password</label>
              {fld("password", "password", "new-password")}
              <p className="text-xs text-[var(--pa-text-dim)] mt-1.5">Min. 6 characters</p>
            </div>

            <label
              htmlFor="signup-baa-checkbox"
              data-testid="signup-baa-label"
              className={`flex items-start gap-3 p-3.5 rounded-md border cursor-pointer transition ${
                baaAccepted
                  ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/5"
                  : "border-[var(--pa-border)] hover:border-[var(--pa-gold)]/60"
              }`}
            >
              <input
                id="signup-baa-checkbox"
                type="checkbox"
                checked={baaAccepted}
                onChange={(e) => setBaaAccepted(e.target.checked)}
                data-testid="signup-baa-checkbox"
                className="mt-0.5 accent-[var(--pa-gold)] shrink-0"
              />
              <span className="text-xs text-[var(--pa-text-muted)] leading-relaxed">
                I agree to the ProsthoAI{" "}
                <Link
                  to="/baa"
                  target="_blank"
                  rel="noopener"
                  data-testid="signup-baa-link"
                  className="text-[var(--pa-gold)] hover:underline underline-offset-2"
                >
                  Business Associate Agreement
                </Link>{" "}
                and{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener"
                  data-testid="signup-terms-link"
                  className="text-[var(--pa-gold)] hover:underline underline-offset-2"
                >
                  Terms of Service
                </Link>{" "}
                governing the handling of Protected Health Information.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !baaAccepted}
              data-testid="signup-submit"
              className="w-full bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-6 py-3 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>
          <p className="mt-6 text-sm text-[var(--pa-text-muted)]">
            Already a member?{" "}
            <Link to="/login" className="text-[var(--pa-gold)] hover:underline" data-testid="link-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
